import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, getAvatarForUser } from '../context/ThemeContext';
import { userAPI, publicAPI, adminAPI } from '../api';
import { CurrencySymbol, CurrencyDisplay } from '../components/CurrencySymbol';
import { PinInput } from '../components/PinInput';
import { PlayerAvatar, RankBadge, FunScore, FunModeToggle } from '../components/FunMode';
import UserTournament from '../components/UserTournament';
import { useVirtualNumpad as useVirtualNumpadHook, VirtualNumpadToggle, ScoreInputWithNumpad } from '../components/VirtualNumpad';
import TelegramLink from '../components/TelegramLink';
import { 
    Users, Send, History, FolderOpen, Trophy, Plus, ChevronLeft, ChevronRight,
    Menu, X, LogOut, RefreshCw, AlertCircle, CheckCircle, Calendar, Edit2, Sun, Moon, Award, Settings, Wallet, PiggyBank, ArrowLeftRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';

const MONTH_NAMES = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

// Helper function to get current Jakarta time (UTC+7)
const getJakartaDate = () => {
    const now = new Date();
    // Convert to Jakarta timezone (UTC+7)
    const jakartaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
    return jakartaTime;
};

// Helper function to get Jakarta date string in YYYY-MM-DD format
const getJakartaDateString = () => {
    const jakartaDate = getJakartaDate();
    const yyyy = jakartaDate.getFullYear();
    const mm = String(jakartaDate.getMonth() + 1).padStart(2, '0');
    const dd = String(jakartaDate.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

// Helper function to format Jakarta date for display
const formatJakartaDate = () => {
    const jakartaDate = getJakartaDate();
    return jakartaDate.toLocaleDateString('id-ID', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        timeZone: 'Asia/Jakarta'
    });
};

// Helper function to round to 1 decimal place
const roundTo1Decimal = (value) => {
    return Math.round(value * 10) / 10;
};

// Helper function to format number - show decimal only if needed
const formatNumber = (value) => {
    const num = parseFloat(value) || 0;
    // If it's a whole number, don't show decimal
    if (Number.isInteger(num)) {
        return num.toString();
    }
    // Otherwise show 1 decimal
    return num.toFixed(1);
};

// Helper function to calculate total score
const calculateTotal = (inputs) => {
    return Object.values(inputs).reduce((sum, val) => {
        const num = parseFloat(val) || 0;
        return sum + roundTo1Decimal(num);
    }, 0);
};

// Helper function to get score color based on value
const getScoreColorClass = (score) => {
    if (score > 0) return 'text-neon-green';
    if (score < 0) return 'text-red-500';
    return 'text-zinc-400';
};

/**
 * Calculate optimal settlement simulation
 * Losers only transfer once (or minimal times), matched to winners with closest amounts
 * This finds the shortest payment paths for faster settlement
 * @param {Array} entries - [{username, balance, user_id}]
 * @returns {Array} - [{from, to, amount}]
 */
const calculateSettlementSimulation = (entries) => {
    // Filter non-zero balances and separate winners/losers
    const winners = entries
        .filter(e => e.balance > 0)
        .map(e => ({...e, remaining: e.balance}))
        .sort((a, b) => a.remaining - b.remaining); // Sort ascending (smallest winner first)
    
    const losers = entries
        .filter(e => e.balance < 0)
        .map(e => ({...e, debt: Math.abs(e.balance)}))
        .sort((a, b) => a.debt - b.debt); // Sort ascending (smallest debt first)
    
    if (winners.length === 0 || losers.length === 0) return [];
    
    const transfers = [];
    
    // Match losers to winners, starting with smallest amounts
    // This creates efficient pairings where similar amounts settle quickly
    let winnerIdx = 0;
    
    for (const loser of losers) {
        let remainingDebt = loser.debt;
        
        while (remainingDebt > 0.001 && winnerIdx < winners.length) {
            const winner = winners[winnerIdx];
            
            if (winner.remaining <= 0.001) {
                winnerIdx++;
                continue;
            }
            
            // Calculate how much this loser pays to this winner
            const amount = roundTo1Decimal(Math.min(remainingDebt, winner.remaining));
            
            if (amount > 0) {
                transfers.push({
                    from: loser.username,
                    from_id: loser.user_id,
                    to: winner.username,
                    to_id: winner.user_id,
                    amount: amount
                });
                
                remainingDebt = roundTo1Decimal(remainingDebt - amount);
                winner.remaining = roundTo1Decimal(winner.remaining - amount);
            }
            
            // If this winner is fully paid, move to next
            if (winner.remaining <= 0.001) {
                winnerIdx++;
            }
        }
    }
    
    return transfers;
};

const UserDashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('leaderboard');
    const [groups, setGroups] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [balance, setBalance] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Transfer modal states
    const [showTransfer, setShowTransfer] = useState(false);
    const [selectedRecipient, setSelectedRecipient] = useState('');
    const [transferAmount, setTransferAmount] = useState('');
    const [showPinModal, setShowPinModal] = useState(false);
    const [transferLoading, setTransferLoading] = useState(false);

    // Leaderboard states
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [leaderboard, setLeaderboard] = useState(null);
    const [availableMonths, setAvailableMonths] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);
    
    // Date range filter states
    const [filterMode, setFilterMode] = useState('single'); // 'single' or 'range'
    const [startMonth, setStartMonth] = useState(1);
    const [startYear, setStartYear] = useState(new Date().getFullYear());
    const [endMonth, setEndMonth] = useState(new Date().getMonth() + 1);
    const [endYear, setEndYear] = useState(new Date().getFullYear());

    // Chart states - removed

    // Score Card creation/edit states
    const [showCreateScoreCard, setShowCreateScoreCard] = useState(false);
    const [scoreCardGroupId, setScoreCardGroupId] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [scoreInputs, setScoreInputs] = useState({});
    const [createScoreCardLoading, setCreateScoreCardLoading] = useState(false);
    const [scoreCardDate, setScoreCardDate] = useState(''); // Format: YYYY-MM-DD
    
    // Edit score card states
    const [showEditScoreCard, setShowEditScoreCard] = useState(false);
    const [editingScoreCard, setEditingScoreCard] = useState(null);
    const [editScoreInputs, setEditScoreInputs] = useState({});
    const [editScoreCardLoading, setEditScoreCardLoading] = useState(false);

    // Settlement batch states
    const [pendingSettlements, setPendingSettlements] = useState({});
    const [settlementLoading, setSettlementLoading] = useState(false);

    // Finance settled states (for users with finance role)
    const [pendingFinanceSettled, setPendingFinanceSettled] = useState({});
    const [financeSettledLoading, setFinanceSettledLoading] = useState(false);

    // Operator Settlement states (NEW)
    const [isOperator, setIsOperator] = useState(false);
    const [showSettleModal, setShowSettleModal] = useState(false);
    const [settleEntries, setSettleEntries] = useState({});
    const [settleNote, setSettleNote] = useState('');
    const [settleLoading, setSettleLoading] = useState(false);
    const [settlements, setSettlements] = useState([]);
    const [showBulkBalanceModal, setShowBulkBalanceModal] = useState(false);
    const [bulkBalanceUsers, setBulkBalanceUsers] = useState([]);
    const [bulkBalanceAmount, setBulkBalanceAmount] = useState('');
    const [bulkBalanceOperation, setBulkBalanceOperation] = useState('set');
    
    // Balance Overview Modal (with settlement simulation)
    const [showBalanceModal, setShowBalanceModal] = useState(false);
    
    // Affects Balance toggle for scorecard
    const [affectsBalance, setAffectsBalance] = useState(true);
    
    // Group settings (TK info)
    const [groupSettings, setGroupSettings] = useState(null);

    const { token, user, logout } = useAuth();
    const { isDark, toggleTheme, isFunMode } = useTheme();
    const { useVirtualNumpad, toggleVirtualNumpad } = useVirtualNumpadHook();
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, [token]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [groupsData, transactionsData, balanceData] = await Promise.all([
                userAPI.getGroups(token),
                userAPI.getTransactions(token),
                userAPI.getBalance(token)
            ]);
            setGroups(groupsData);
            setTransactions(transactionsData);
            setBalance(balanceData.balance);
            
            // Set default selected group for leaderboard
            if (groupsData.length > 0 && !selectedGroup) {
                setSelectedGroup(groupsData[0]);
            }
        } catch (err) {
            setError('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Load leaderboard when group or month changes
    useEffect(() => {
        if (selectedGroup && activeTab === 'leaderboard') {
            loadLeaderboard();
            loadAvailableMonths();
            checkOperatorStatus();
            loadGroupSettings();
        }
    }, [selectedGroup, selectedMonth, selectedYear, activeTab, filterMode, startMonth, startYear, endMonth, endYear]);

    // Load settlements when operator status changes
    useEffect(() => {
        if (isOperator && selectedGroup) {
            loadSettlements();
        }
    }, [isOperator, selectedGroup]);

    // Check if current user is an operator of selected group
    const checkOperatorStatus = async () => {
        if (!selectedGroup) return;
        try {
            const result = await adminAPI.checkOperator(token, selectedGroup.id);
            setIsOperator(result.is_operator);
        } catch (err) {
            setIsOperator(false);
        }
    };

    // Load group settings (for TK info) - this also sets operator status
    const loadGroupSettings = async () => {
        if (!selectedGroup) return;
        try {
            // Use userAPI for users (works for all users in the group)
            const settings = await userAPI.getGroupSettings(token, selectedGroup.id);
            setGroupSettings(settings);
            // Also set isOperator from the response
            if (settings.is_operator !== undefined) {
                setIsOperator(settings.is_operator);
            }
        } catch (err) {
            console.error('Failed to load group settings:', err);
            setGroupSettings(null);
            // Fallback to admin API check
            checkOperatorStatus();
        }
    };

    // Load settlements for operator
    const loadSettlements = async () => {
        if (!selectedGroup || !isOperator) return;
        try {
            const data = await adminAPI.getSettlements(token, selectedGroup.id);
            setSettlements(data);
        } catch (err) {
            console.error('Failed to load settlements:', err);
        }
    };

    // Handle settlement submission (Operator only)
    const handleCreateSettlement = async () => {
        const entries = Object.entries(settleEntries)
            .filter(([_, amount]) => amount !== '' && parseFloat(amount) !== 0)
            .map(([userId, amount]) => ({
                user_id: userId,
                amount: parseFloat(amount)
            }));
        
        if (entries.length < 2) {
            setError('Pilih minimal 2 pemain dengan jumlah');
            return;
        }
        
        const total = entries.reduce((sum, e) => sum + e.amount, 0);
        if (Math.abs(total) > 0.01) {
            setError(`Total harus 0, sekarang: ${total}`);
            return;
        }
        
        setSettleLoading(true);
        try {
            await adminAPI.createSettlement(token, selectedGroup.id, entries, settleNote);
            setSuccess('Settlement berhasil disimpan');
            setShowSettleModal(false);
            setSettleEntries({});
            setSettleNote('');
            loadData(); // Reload balances
            loadSettlements();
        } catch (err) {
            setError(err.response?.data?.detail || 'Gagal menyimpan settlement');
        } finally {
            setSettleLoading(false);
        }
    };

    // Handle bulk balance update (Operator only)
    const handleBulkBalance = async () => {
        if (bulkBalanceUsers.length === 0) {
            setError('Pilih minimal 1 pemain');
            return;
        }
        // Allow any value including 0 and negative
        if (bulkBalanceAmount === '' || bulkBalanceAmount === null) {
            setError('Masukkan jumlah saldo');
            return;
        }
        
        setSettleLoading(true);
        try {
            await adminAPI.updateBulkBalance(
                token, 
                selectedGroup.id, 
                bulkBalanceUsers, 
                parseFloat(bulkBalanceAmount),
                bulkBalanceOperation
            );
            setSuccess(`Saldo ${bulkBalanceUsers.length} pemain berhasil diupdate`);
            setShowBulkBalanceModal(false);
            setBulkBalanceUsers([]);
            setBulkBalanceAmount('');
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Gagal update saldo');
        } finally {
            setSettleLoading(false);
        }
    };

    const toggleBulkBalanceUser = (userId) => {
        setBulkBalanceUsers(prev => 
            prev.includes(userId) 
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const loadLeaderboard = async () => {
        if (!selectedGroup) return;
        setLeaderboardLoading(true);
        setPendingSettlements({}); // Reset pending settlements when loading new data
        setPendingFinanceSettled({}); // Reset pending finance settled
        try {
            let data;
            if (filterMode === 'range') {
                data = await userAPI.getLeaderboardRange(token, selectedGroup.id, startMonth, startYear, endMonth, endYear);
            } else {
                data = await userAPI.getLeaderboard(token, selectedGroup.id, selectedMonth, selectedYear);
            }
            setLeaderboard(data);
        } catch (err) {
            setError('Failed to load leaderboard');
        } finally {
            setLeaderboardLoading(false);
        }
    };

    // Toggle settlement status (batch mode)
    const handleToggleSettlement = (scorecardId, currentStatus) => {
        setPendingSettlements(prev => {
            const newPending = { ...prev };
            if (newPending[scorecardId] !== undefined) {
                // If already in pending, remove it (cancel the change)
                delete newPending[scorecardId];
            } else {
                // Add to pending with the new status (toggle)
                newPending[scorecardId] = !currentStatus;
            }
            return newPending;
        });
    };

    // Submit all pending settlements
    const handleSubmitSettlements = async () => {
        const pendingIds = Object.keys(pendingSettlements);
        if (pendingIds.length === 0) return;

        setSettlementLoading(true);
        try {
            // Call API for each pending settlement
            await Promise.all(
                pendingIds.map(id => userAPI.toggleSettlement(token, id))
            );
            setSuccess(`${pendingIds.length} score card berhasil di-update!`);
            setPendingSettlements({});
            // Reload leaderboard to get updated data
            loadLeaderboard();
        } catch (err) {
            console.error('Failed to submit settlements:', err);
            setError('Gagal mengubah status settlement');
        } finally {
            setSettlementLoading(false);
        }
    };

    // Cancel all pending settlements
    const handleCancelSettlements = () => {
        setPendingSettlements({});
    };

    // Check if a scorecard has pending change
    const getSettlementStatus = (scorecardId, originalStatus) => {
        if (pendingSettlements[scorecardId] !== undefined) {
            return pendingSettlements[scorecardId];
        }
        return originalStatus;
    };

    // Check if a scorecard has pending change
    const hasPendingChange = (scorecardId) => {
        return pendingSettlements[scorecardId] !== undefined;
    };

    // Finance settled functions (for users with finance role)
    const handleToggleFinanceSettled = (scorecardId, currentlySettled) => {
        setPendingFinanceSettled(prev => {
            const newPending = { ...prev };
            if (newPending[scorecardId] !== undefined) {
                delete newPending[scorecardId];
            } else {
                newPending[scorecardId] = !currentlySettled;
            }
            return newPending;
        });
    };

    const handleSubmitFinanceSettled = async () => {
        const pendingIds = Object.keys(pendingFinanceSettled);
        if (pendingIds.length === 0) return;

        setFinanceSettledLoading(true);
        try {
            await Promise.all(
                pendingIds.map(id => userAPI.toggleFinanceSettled(token, id))
            );
            setSuccess(`${pendingIds.length} status finance berhasil di-update!`);
            setPendingFinanceSettled({});
            loadLeaderboard();
        } catch (err) {
            console.error('Failed to submit finance settled:', err);
            setError('Gagal mengubah status finance');
        } finally {
            setFinanceSettledLoading(false);
        }
    };

    const handleCancelFinanceSettled = () => {
        setPendingFinanceSettled({});
    };

    const getFinanceSettledStatus = (scorecardId, financeSettledList) => {
        if (pendingFinanceSettled[scorecardId] !== undefined) {
            return pendingFinanceSettled[scorecardId];
        }
        return financeSettledList?.includes(user?.id) || false;
    };

    const hasFinancePendingChange = (scorecardId) => {
        return pendingFinanceSettled[scorecardId] !== undefined;
    };

    const loadAvailableMonths = async () => {
        if (!selectedGroup) return;
        try {
            const months = await userAPI.getAvailableMonths(token, selectedGroup.id);
            setAvailableMonths(months);
        } catch (err) {
            console.error('Failed to load available months');
        }
    };

    // Get all users from all groups (unique)
    const getAllGroupMembers = () => {
        const members = new Map();
        groups.forEach(g => {
            g.members.forEach(m => {
                if (m.id !== user?.id) {
                    members.set(m.id, m);
                }
            });
        });
        return Array.from(members.values());
    };

    // Get members of a specific group
    const getGroupMembers = (groupId) => {
        const group = groups.find(g => g.id === groupId);
        return group?.members || [];
    };

    const handleTransferClick = () => {
        setShowTransfer(true);
    };

    const handleTransferSubmit = (e) => {
        e.preventDefault();
        
        const amount = parseFloat(transferAmount);
        if (amount > balance) {
            setError('Insufficient balance');
            return;
        }
        if (!selectedRecipient) {
            setError('Please select a recipient');
            return;
        }
        
        setShowTransfer(false);
        setShowPinModal(true);
    };

    const handlePinComplete = async (pin) => {
        setTransferLoading(true);
        try {
            await userAPI.transfer(token, selectedRecipient, parseFloat(transferAmount), pin);
            setSuccess('Transfer successful!');
            setShowPinModal(false);
            setSelectedRecipient('');
            setTransferAmount('');
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Transfer failed');
            setShowPinModal(false);
        } finally {
            setTransferLoading(false);
        }
    };

    // Score Card functions
    const handleOpenCreateScoreCard = (groupId) => {
        setScoreCardGroupId(groupId);
        setSelectedUsers([]);
        setScoreInputs({});
        // Set default date to today in Jakarta timezone (YYYY-MM-DD format)
        setScoreCardDate(getJakartaDateString());
        setShowCreateScoreCard(true);
    };

    const handleUserSelect = (userId, username) => {
        if (selectedUsers.find(u => u.id === userId)) {
            setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
            const newInputs = { ...scoreInputs };
            delete newInputs[userId];
            setScoreInputs(newInputs);
        } else {
            setSelectedUsers([...selectedUsers, { id: userId, username }]);
            setScoreInputs({ ...scoreInputs, [userId]: '' });
        }
    };

    const handleScoreChange = (userId, value) => {
        // Replace comma with period for decimal separator (for Indonesian keyboards)
        const normalizedValue = value.replace(',', '.');
        // Validate: only allow numbers with max 1 decimal place
        if (normalizedValue === '' || normalizedValue === '-' || /^-?\d*\.?\d?$/.test(normalizedValue)) {
            setScoreInputs({ ...scoreInputs, [userId]: normalizedValue });
        }
    };

    // Toggle minus sign for score input (for mobile)
    const toggleScoreMinus = (userId) => {
        const currentVal = scoreInputs[userId] || '';
        if (currentVal.startsWith('-')) {
            setScoreInputs({...scoreInputs, [userId]: currentVal.substring(1)});
        } else if (currentVal === '') {
            setScoreInputs({...scoreInputs, [userId]: '-'});
        } else {
            setScoreInputs({...scoreInputs, [userId]: '-' + currentVal});
        }
    };

    // Check if total score is zero (for validation)
    const isScoreTotalZero = (inputs) => {
        const total = calculateTotal(inputs);
        return Math.abs(roundTo1Decimal(total)) < 0.01;
    };

    const handleCreateScoreCard = async () => {
        if (selectedUsers.length === 0) {
            setError('Pilih minimal satu user');
            return;
        }

        // Validate date is selected
        if (!scoreCardDate) {
            setError('Pilih tanggal score card');
            return;
        }

        // Validate all scores are filled
        for (const u of selectedUsers) {
            if (!scoreInputs[u.id] || scoreInputs[u.id] === '') {
                setError(`Masukkan skor untuk ${u.username}`);
                return;
            }
        }

        // Validate total is zero
        if (!isScoreTotalZero(scoreInputs)) {
            setError('Total skor harus sama dengan 0');
            return;
        }

        setCreateScoreCardLoading(true);
        try {
            const entries = selectedUsers.map(u => ({
                user_id: u.id,
                username: u.username,
                score: roundTo1Decimal(parseFloat(scoreInputs[u.id]))
            }));

            await userAPI.createScoreCard(token, scoreCardGroupId, entries, scoreCardDate, affectsBalance);
            setSuccess('Score card berhasil dibuat!');
            setShowCreateScoreCard(false);
            setSelectedUsers([]);
            setScoreInputs({});
            setScoreCardDate('');
            setAffectsBalance(true);  // Reset to default ON for next scorecard
            
            // Reload leaderboard if on that tab
            if (activeTab === 'leaderboard' && selectedGroup?.id === scoreCardGroupId) {
                loadLeaderboard();
            }
            // Reload data to update balances
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Gagal membuat score card');
        } finally {
            setCreateScoreCardLoading(false);
        }
    };

    // Edit Score Card functions
    const handleOpenEditScoreCard = (scorecard) => {
        setEditingScoreCard(scorecard);
        const inputs = {};
        scorecard.entries.forEach(entry => {
            inputs[entry.user_id] = entry.score.toString();
        });
        setEditScoreInputs(inputs);
        setShowEditScoreCard(true);
    };

    const handleEditScoreChange = (userId, value) => {
        // Replace comma with period for decimal separator
        const normalizedValue = value.replace(',', '.');
        // Validate: only allow numbers with max 1 decimal place
        if (normalizedValue === '' || normalizedValue === '-' || /^-?\d*\.?\d?$/.test(normalizedValue)) {
            setEditScoreInputs({ ...editScoreInputs, [userId]: normalizedValue });
        }
    };

    // Toggle minus for edit score
    const toggleEditScoreMinus = (userId) => {
        const currentVal = editScoreInputs[userId] || '';
        if (currentVal.startsWith('-')) {
            setEditScoreInputs({...editScoreInputs, [userId]: currentVal.substring(1)});
        } else if (currentVal === '') {
            setEditScoreInputs({...editScoreInputs, [userId]: '-'});
        } else {
            setEditScoreInputs({...editScoreInputs, [userId]: '-' + currentVal});
        }
    };

    const handleUpdateScoreCard = async () => {
        if (!editingScoreCard) return;

        // Validate all scores are filled
        for (const entry of editingScoreCard.entries) {
            if (!editScoreInputs[entry.user_id] || editScoreInputs[entry.user_id] === '') {
                setError(`Masukkan skor untuk ${entry.username}`);
                return;
            }
        }

        // Validate total is zero
        if (!isScoreTotalZero(editScoreInputs)) {
            setError('Total skor harus sama dengan 0');
            return;
        }

        setEditScoreCardLoading(true);
        try {
            const entries = editingScoreCard.entries.map(entry => ({
                user_id: entry.user_id,
                username: entry.username,
                score: roundTo1Decimal(parseFloat(editScoreInputs[entry.user_id]))
            }));

            await userAPI.updateScoreCard(token, editingScoreCard.id, entries);
            setSuccess('Score card berhasil diupdate!');
            setShowEditScoreCard(false);
            setEditingScoreCard(null);
            setEditScoreInputs({});
            
            // Reload leaderboard
            loadLeaderboard();
        } catch (err) {
            setError(err.response?.data?.detail || 'Gagal mengupdate score card');
        } finally {
            setEditScoreCardLoading(false);
        }
    };

    const handleMonthChange = (direction) => {
        let newMonth = selectedMonth + direction;
        let newYear = selectedYear;
        
        if (newMonth > 12) {
            newMonth = 1;
            newYear++;
        } else if (newMonth < 1) {
            newMonth = 12;
            newYear--;
        }
        
        setSelectedMonth(newMonth);
        setSelectedYear(newYear);
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
    };

    const getRecipientUsername = (id) => {
        const members = getAllGroupMembers();
        const found = members.find(m => m.id === id);
        return found?.username || id;
    };

    // Clear messages after 3 seconds
    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError('');
                setSuccess('');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    return (
        <div className="dashboard-layout">
            {/* Mobile menu button */}
            <button 
                className="mobile-menu-btn lg:hidden"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="mobile-menu-btn"
            >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Sidebar overlay */}
            <div 
                className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
                onClick={() => setSidebarOpen(false)}
            />

            {/* Sidebar */}
            <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="flex-shrink-0">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-md bg-neon-green/10 border border-neon-green/30 flex items-center justify-center">
                                <CurrencySymbol size="md" className="text-neon-green" />
                            </div>
                            <div>
                                <h1 className="text-sm font-headings font-bold text-white">VG WALLET</h1>
                                <p className="text-xs text-zinc-500 font-mono">USER</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Fun Mode Toggle */}
                            <FunModeToggle />
                            {/* Theme Toggle */}
                            <button
                                onClick={toggleTheme}
                                className="p-2 rounded-md hover:bg-zinc-800 transition-colors"
                                data-testid="theme-toggle-btn"
                                title={isDark ? 'Mode Terang' : 'Mode Gelap'}
                            >
                                {isDark ? (
                                    <Sun className="w-5 h-5 text-yellow-400" />
                                ) : (
                                    <Moon className="w-5 h-5 text-zinc-600" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Balance Card */}
                    <div className="stat-card-glow mb-6">
                        <p className="text-xs text-zinc-500 font-mono uppercase mb-1">Your Balance</p>
                        <CurrencyDisplay amount={balance} size="lg" className="text-neon-green" />
                        <Button
                            onClick={handleTransferClick}
                            className="w-full mt-4 bg-neon-green hover:bg-neon-green/90 text-void-black"
                            disabled={balance <= 0 || getAllGroupMembers().length === 0}
                            data-testid="transfer-btn"
                        >
                            <Send className="w-4 h-4 mr-2" />
                            Transfer
                        </Button>
                    </div>

                    <nav className="space-y-1">
                        <button
                            onClick={() => { setActiveTab('groups'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'groups' ? 'active' : ''}`}
                            data-testid="nav-groups"
                        >
                            <FolderOpen className="w-5 h-5" />
                            <span>My Groups</span>
                        </button>
                        <button
                            onClick={() => { setActiveTab('leaderboard'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'leaderboard' ? 'active' : ''}`}
                            data-testid="nav-leaderboard"
                        >
                            <Trophy className="w-5 h-5" />
                            <span>Klasemen</span>
                        </button>
                        <button
                            onClick={() => { setActiveTab('tournament'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'tournament' ? 'active' : ''}`}
                            data-testid="nav-tournament"
                        >
                            <Award className="w-5 h-5" />
                            <span>Tournament</span>
                        </button>
                        <button
                            onClick={() => { setActiveTab('transactions'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'transactions' ? 'active' : ''}`}
                            data-testid="nav-transactions"
                        >
                            <History className="w-5 h-5" />
                            <span>Transactions</span>
                        </button>
                        <button
                            onClick={() => { setActiveTab('settings'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'settings' ? 'active' : ''}`}
                            data-testid="nav-settings"
                        >
                            <Settings className="w-5 h-5" />
                            <span>Pengaturan</span>
                        </button>
                        {/* Logout button moved below transactions */}
                        <button
                            onClick={handleLogout}
                            className="nav-item w-full text-left text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-5 h-5" />
                            <span>Logout</span>
                        </button>
                    </nav>
                </div>

                <div className="flex-grow"></div>

                <div className="flex-shrink-0 pt-4 border-t border-zinc-800">
                    <div className="px-3 py-2">
                        <p className="text-xs text-zinc-500 font-mono">LOGGED IN AS</p>
                        <p className="text-sm text-white font-medium">{user?.username}</p>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <main className="main-content">
                {/* Notifications */}
                {error && (
                    <div className="fixed top-4 right-4 z-50 flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 animate-slide-up">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                    </div>
                )}
                {success && (
                    <div className="fixed top-4 right-4 z-50 flex items-center gap-2 p-4 bg-neon-green/10 border border-neon-green/20 rounded-md text-neon-green animate-slide-up">
                        <CheckCircle className="w-4 h-4" />
                        {success}
                    </div>
                )}

                {/* Groups Tab */}
                {activeTab === 'groups' && (
                    <div>
                        <div className="page-header flex items-start justify-between">
                            <div>
                                <h1 className="page-title">My Groups</h1>
                                <p className="page-subtitle">Grup tempat Anda terdaftar oleh admin</p>
                            </div>
                            <Button
                                onClick={loadData}
                                variant="outline"
                                size="sm"
                                className="border-zinc-700 text-zinc-400 hover:text-white"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <span className="spinner" />
                            </div>
                        ) : groups.length === 0 ? (
                            <div className="empty-state">
                                <FolderOpen className="empty-state-icon mx-auto" />
                                <p>Anda belum ditambahkan ke grup manapun</p>
                                <p className="text-xs text-zinc-600 mt-2">Contact your admin to be added to a group</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {groups.map((g) => (
                                    <div key={g.id} className="stat-card card-hover" data-testid={`group-card-${g.id}`}>
                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <h3 className="text-xl font-headings font-semibold text-white">{g.name}</h3>
                                                <p className="text-sm text-zinc-500">{g.members.length} members</p>
                                            </div>
                                            <Button
                                                onClick={() => handleOpenCreateScoreCard(g.id)}
                                                size="sm"
                                                className="bg-neon-green/10 text-neon-green hover:bg-neon-green/20 border border-neon-green/20"
                                                data-testid={`create-scorecard-btn-${g.id}`}
                                            >
                                                <Plus className="w-4 h-4 mr-1" />
                                                Score Card
                                            </Button>
                                        </div>
                                        
                                        {g.members.length > 0 && (
                                            <div className="border-t border-zinc-800 pt-4">
                                                <p className="text-xs text-zinc-500 font-mono uppercase mb-3">Members</p>
                                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                                    {g.members.map(member => (
                                                        <div 
                                                            key={member.id} 
                                                            className={`p-3 rounded-md ${member.id === user?.id ? 'bg-neon-green/10 border border-neon-green/30' : 'bg-zinc-800/50'}`}
                                                        >
                                                            <p className="text-sm font-medium text-white">
                                                                {member.username}
                                                                {member.id === user?.id && <span className="text-neon-green text-xs ml-1">(You)</span>}
                                                            </p>
                                                            <p className="text-xs text-zinc-500 mt-1">
                                                                <CurrencyDisplay amount={member.balance} size="sm" className="text-zinc-400" />
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Leaderboard Tab */}
                {activeTab === 'leaderboard' && (
                    <div className="overflow-x-hidden">
                        <div className="page-header">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h1 className="page-title flex items-center gap-2">
                                        <Trophy className="w-6 h-6 text-yellow-500" />
                                        Klasemen
                                    </h1>
                                    <p className="page-subtitle">Leaderboard & Score Cards</p>
                                    {/* Current Jakarta date display */}
                                    <p className="text-sm font-semibold text-cyan-400 mt-1" style={{textShadow: '0 0 8px #22d3ee'}}>
                                        Hari ini: {formatJakartaDate()} (WIB)
                                    </p>
                                </div>
                                <Button
                                    onClick={loadLeaderboard}
                                    variant="outline"
                                    size="sm"
                                    className="border-zinc-700 text-zinc-400 hover:text-white"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                </Button>
                            </div>

                            {/* Group Selector */}
                            {groups.length > 0 && (
                                <div className="flex flex-wrap gap-4 mb-6">
                                    <div>
                                        <Label className="form-label mb-2 block">Select Group</Label>
                                        <Select 
                                            value={selectedGroup?.id || ''} 
                                            onValueChange={(value) => {
                                                const group = groups.find(g => g.id === value);
                                                setSelectedGroup(group);
                                            }}
                                        >
                                            <SelectTrigger className="w-[200px] bg-zinc-800 border-zinc-700">
                                                <SelectValue placeholder="Select group" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-800 border-zinc-700">
                                                {groups.map(g => (
                                                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Filter Mode Toggle */}
                                    <div>
                                        <Label className="form-label mb-2 block">Filter Mode</Label>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setFilterMode('single')}
                                                className={`border-zinc-700 ${filterMode === 'single' ? 'bg-neon-green/20 text-neon-green border-neon-green/30' : 'text-zinc-400 hover:text-white'}`}
                                            >
                                                Per Bulan
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setFilterMode('range')}
                                                className={`border-zinc-700 ${filterMode === 'range' ? 'bg-neon-green/20 text-neon-green border-neon-green/30' : 'text-zinc-400 hover:text-white'}`}
                                            >
                                                Rentang Waktu
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Single Month Navigator */}
                            {filterMode === 'single' && (
                                <div className="mb-6">
                                    <Label className="form-label mb-2 block">Bulan</Label>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleMonthChange(-1)}
                                            className="border-zinc-700 text-zinc-400 hover:text-white"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </Button>
                                        <div className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-md min-w-[180px] text-center">
                                            <span className="text-white font-medium">
                                                {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
                                            </span>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleMonthChange(1)}
                                            className="border-zinc-700 text-zinc-400 hover:text-white"
                                            disabled={selectedMonth === new Date().getMonth() + 1 && selectedYear === new Date().getFullYear()}
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Date Range Filter */}
                            {filterMode === 'range' && (
                                <div className="mb-6 p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                                    <Label className="form-label mb-3 block">Rentang Waktu</Label>
                                    <div className="flex flex-wrap items-end gap-4">
                                        <div>
                                            <Label className="text-xs text-zinc-500 mb-1 block">Dari</Label>
                                            <div className="flex gap-2">
                                                <Select value={startMonth.toString()} onValueChange={(v) => setStartMonth(parseInt(v))}>
                                                    <SelectTrigger className="w-[120px] bg-zinc-800 border-zinc-700">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-zinc-800 border-zinc-700">
                                                        {MONTH_NAMES.map((m, idx) => (
                                                            <SelectItem key={idx} value={(idx + 1).toString()}>{m}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <Select value={startYear.toString()} onValueChange={(v) => setStartYear(parseInt(v))}>
                                                    <SelectTrigger className="w-[90px] bg-zinc-800 border-zinc-700">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-zinc-800 border-zinc-700">
                                                        {[2024, 2025, 2026, 2027].map(y => (
                                                            <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <div className="text-zinc-500">s/d</div>
                                        <div>
                                            <Label className="text-xs text-zinc-500 mb-1 block">Sampai</Label>
                                            <div className="flex gap-2">
                                                <Select value={endMonth.toString()} onValueChange={(v) => setEndMonth(parseInt(v))}>
                                                    <SelectTrigger className="w-[120px] bg-zinc-800 border-zinc-700">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-zinc-800 border-zinc-700">
                                                        {MONTH_NAMES.map((m, idx) => (
                                                            <SelectItem key={idx} value={(idx + 1).toString()}>{m}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <Select value={endYear.toString()} onValueChange={(v) => setEndYear(parseInt(v))}>
                                                    <SelectTrigger className="w-[90px] bg-zinc-800 border-zinc-700">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-zinc-800 border-zinc-700">
                                                        {[2024, 2025, 2026, 2027].map(y => (
                                                            <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Quick Month Selection - only for single mode */}
                            {filterMode === 'single' && availableMonths.length > 0 && (
                                <div className="mb-6">
                                    <Label className="form-label mb-2 block">Quick Select</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {availableMonths.slice(0, 6).map((m, idx) => (
                                            <Button
                                                key={idx}
                                                variant="outline"
                                                size="sm"
                                                onClick={() => { setSelectedMonth(m.month); setSelectedYear(m.year); }}
                                                className={`border-zinc-700 ${selectedMonth === m.month && selectedYear === m.year ? 'bg-neon-green/20 text-neon-green border-neon-green/30' : 'text-zinc-400 hover:text-white'}`}
                                            >
                                                {MONTH_NAMES[m.month - 1]} {m.year}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {!selectedGroup ? (
                            <div className="empty-state">
                                <Trophy className="empty-state-icon mx-auto" />
                                <p>Select a group to view leaderboard</p>
                            </div>
                        ) : leaderboardLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <span className="spinner" />
                            </div>
                        ) : !leaderboard ? (
                            <div className="empty-state">
                                <Trophy className="empty-state-icon mx-auto" />
                                <p>No data available</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Create Score Card & Operator Buttons */}
                                <div className="flex flex-wrap justify-between items-center gap-2">
                                    {/* Operator Controls */}
                                    {isOperator && (
                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                onClick={() => {
                                                    setSettleEntries({});
                                                    setSettleNote('');
                                                    setShowSettleModal(true);
                                                }}
                                                size="sm"
                                                className="bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/20"
                                                data-testid="settle-btn"
                                            >
                                                <ArrowLeftRight className="w-4 h-4 mr-1" />
                                                Settle
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    setBulkBalanceUsers([]);
                                                    setBulkBalanceAmount('');
                                                    setShowBulkBalanceModal(true);
                                                }}
                                                size="sm"
                                                className="bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 border border-yellow-500/20"
                                                data-testid="bulk-balance-btn"
                                            >
                                                <Wallet className="w-4 h-4 mr-1" />
                                                Set Saldo
                                            </Button>
                                            <Button
                                                onClick={() => setShowBalanceModal(true)}
                                                size="sm"
                                                className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20"
                                                data-testid="balance-overview-btn"
                                            >
                                                <PiggyBank className="w-4 h-4 mr-1" />
                                                Balance
                                            </Button>
                                        </div>
                                    )}
                                    <Button
                                        onClick={() => handleOpenCreateScoreCard(selectedGroup.id)}
                                        className="bg-neon-green hover:bg-neon-green/90 text-void-black"
                                        data-testid="create-scorecard-btn"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Create Score Card
                                    </Button>
                                </div>

                                {/* Leaderboard Table */}
                                <div className="stat-card">
                                    <h3 className="text-lg font-headings font-semibold text-white mb-4 flex items-center gap-2">
                                        <Trophy className={`w-5 h-5 ${isFunMode ? 'animate-bounce' : ''} text-yellow-500`} />
                                        Rankings - {leaderboard.group_name}
                                        {isFunMode && <span className="text-sm">🏆</span>}
                                    </h3>
                                    <div className="overflow-x-auto">
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>{isFunMode ? '🎖️' : 'Rank'}</th>
                                                    <th>Saldo</th>
                                                    <th>{isFunMode ? '🎮 Player' : 'Username'}</th>
                                                    <th>{isFunMode ? '⭐ Score' : 'Total Score'}</th>
                                                    <th>{isFunMode ? '🎯' : 'Games'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {leaderboard.entries.map((entry, idx) => {
                                                    const isCurrentUser = entry.user_id === user?.id;
                                                    const avatar = getAvatarForUser(entry.username);
                                                    
                                                    return (
                                                        <tr 
                                                            key={entry.user_id}
                                                            className={`${isCurrentUser ? 'bg-neon-green/5' : ''} ${isFunMode ? 'fun-row-hover' : ''}`}
                                                            style={isFunMode ? { '--row-glow': avatar.color } : {}}
                                                        >
                                                            <td>
                                                                <RankBadge rank={idx + 1} showTitle={isFunMode && idx < 3} />
                                                            </td>
                                                            <td>
                                                                <span className={`font-mono font-semibold ${
                                                                    (entry.balance || 0) > 0 ? 'text-neon-green' : 
                                                                    (entry.balance || 0) < 0 ? 'text-red-400' : 'text-zinc-400'
                                                                }`}>
                                                                    {formatNumber(entry.balance || 0)}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="flex items-center gap-2">
                                                                    <PlayerAvatar username={entry.username} size="md" />
                                                                    <span className={`font-bold uppercase ${isCurrentUser ? 'text-neon-green' : 'text-white'}`}
                                                                        style={isFunMode ? { textShadow: `0 0 10px ${avatar.color}` } : {}}>
                                                                        {entry.username.toUpperCase()}
                                                                        {isCurrentUser && (
                                                                            <span className={`ml-2 ${isFunMode ? 'fun-you-badge text-xs bg-neon-green/20 text-neon-green px-2 py-0.5 rounded-full' : ''}`}>
                                                                                (YOU)
                                                                            </span>
                                                                        )}
                                                                        {/* TK Badge */}
                                                                        {groupSettings?.tk_user_id === entry.user_id && (
                                                                            <span className="ml-2 text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">
                                                                                KAS
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                    {isFunMode && idx === 0 && (
                                                                        <span className="text-xs text-yellow-400 fun-champion-text">🔥</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <FunScore score={entry.total_score} size="md" />
                                                            </td>
                                                            <td className="text-zinc-500">
                                                                {isFunMode ? `🎯 ${entry.score_count}` : entry.score_count}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Settlement History (Operator Only) */}
                                {isOperator && settlements.length > 0 && (
                                    <div className="stat-card">
                                        <h3 className="text-lg font-headings font-semibold text-white mb-4 flex items-center gap-2">
                                            <History className="w-5 h-5 text-blue-400" />
                                            Settlement History ({settlements.length})
                                        </h3>
                                        <div className="space-y-2 max-h-60 overflow-y-auto">
                                            {settlements.map((s, idx) => (
                                                <div key={s.id || idx} className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <span className="text-xs text-zinc-500">
                                                            {new Date(s.created_at).toLocaleDateString('id-ID')} {new Date(s.created_at).toLocaleTimeString('id-ID', {hour: '2-digit', minute: '2-digit'})}
                                                        </span>
                                                        <span className="text-xs text-zinc-600">
                                                            by <span className="uppercase text-zinc-400">{s.created_by_username}</span>
                                                        </span>
                                                    </div>
                                                    {s.type === 'bulk_balance' ? (
                                                        <div className="text-sm">
                                                            <span className="text-yellow-400">Set Saldo</span>
                                                            <span className="text-zinc-400"> {s.operation === 'set' ? '=' : '+'}{s.amount} untuk {s.user_count} pemain</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-2">
                                                            {s.entries?.map((e, i) => (
                                                                <span key={i} className={`text-sm font-mono ${e.amount >= 0 ? 'text-neon-green' : 'text-red-400'}`}>
                                                                    <span className="uppercase text-white">{e.username}</span> {e.amount >= 0 ? '+' : ''}{e.amount}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {s.note && (
                                                        <p className="text-xs text-zinc-500 mt-1 italic">&ldquo;{s.note}&rdquo;</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Score Cards List - Newest first at top, but oldest gets #1 */}
                                {leaderboard.scorecards.length > 0 && (
                                    <div>
                                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3 sm:mb-4">
                                            <div className="flex items-center gap-2 sm:gap-3">
                                                <h3 className="text-base sm:text-lg font-headings font-semibold text-white">
                                                    {isFunMode ? '🃏 ' : ''}Score Cards ({leaderboard.scorecards.length})
                                                </h3>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleOpenCreateScoreCard(selectedGroup.id)}
                                                    className="bg-neon-green hover:bg-neon-green/90 text-void-black h-7 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm"
                                                    data-testid="create-scorecard-inline-btn"
                                                >
                                                    <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                                                    <span className="ml-1 hidden sm:inline">Create</span>
                                                </Button>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                {/* Finance Settled Batch Actions */}
                                                {user?.is_finance && Object.keys(pendingFinanceSettled).length > 0 && (
                                                    <>
                                                        <span className="text-sm text-orange-400">
                                                            💰 {Object.keys(pendingFinanceSettled).length} finance pending
                                                        </span>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={handleCancelFinanceSettled}
                                                            className="border-zinc-600 text-zinc-400 hover:bg-zinc-800"
                                                        >
                                                            Batal
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            onClick={handleSubmitFinanceSettled}
                                                            disabled={financeSettledLoading}
                                                            className="bg-yellow-500 hover:bg-yellow-600 text-black"
                                                            data-testid="submit-finance-settled-btn"
                                                        >
                                                            {financeSettledLoading ? (
                                                                <span className="flex items-center gap-1">
                                                                    <span className="spinner w-3 h-3" />
                                                                    Menyimpan...
                                                                </span>
                                                            ) : (
                                                                <>💰 Simpan Finance</>
                                                            )}
                                                        </Button>
                                                    </>
                                                )}
                                                {/* Settlement Batch Actions */}
                                                {Object.keys(pendingSettlements).length > 0 && (
                                                    <>
                                                        <span className="text-sm text-yellow-400">
                                                            {Object.keys(pendingSettlements).length} settlement pending
                                                        </span>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={handleCancelSettlements}
                                                            className="border-zinc-600 text-zinc-400 hover:bg-zinc-800"
                                                        >
                                                            Batal
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            onClick={handleSubmitSettlements}
                                                            disabled={settlementLoading}
                                                            className="bg-blue-500 hover:bg-blue-600 text-white"
                                                            data-testid="submit-settlements-btn"
                                                        >
                                                            {settlementLoading ? (
                                                                <span className="flex items-center gap-1">
                                                                    <span className="spinner w-3 h-3" />
                                                                    Menyimpan...
                                                                </span>
                                                            ) : (
                                                                <>Simpan Settlement</>
                                                            )}
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 pt-3 pl-3">
                                            {leaderboard.scorecards.map((sc, cardIndex) => {
                                                const isCreator = sc.created_by === user?.id;
                                                const originalSettled = sc.is_settled;
                                                const displaySettled = getSettlementStatus(sc.id, originalSettled);
                                                const isPending = hasPendingChange(sc.id);
                                                
                                                return (
                                                    <div 
                                                        key={sc.id} 
                                                        className={`stat-card relative ${isFunMode ? 'scorecard-item' : ''} ${displaySettled ? 'settled-card' : ''} ${isPending ? 'ring-2 ring-yellow-500/50' : ''}`}
                                                        style={displaySettled ? { backgroundColor: 'rgba(59, 130, 246, 0.15)', borderColor: 'rgba(59, 130, 246, 0.3)' } : {}}
                                                        data-testid={`scorecard-${sc.id}`}
                                                    >
                                                        {/* Score Card Number */}
                                                        <div className={`absolute -top-2 -left-2 w-7 h-7 sm:w-9 sm:h-9 rounded-full ${displaySettled ? 'bg-blue-500' : 'bg-neon-green'} flex items-center justify-center shadow-lg z-10 ${isFunMode ? 'fun-rank-pulse' : ''}`}>
                                                            <span className="text-xs sm:text-base font-bold text-void-black">#{leaderboard.scorecards.length - cardIndex}</span>
                                                        </div>
                                                        
                                                        {/* Settlement Badge - smaller on mobile */}
                                                        {displaySettled && (
                                                            <div className={`absolute -top-1 -right-1 sm:-top-2 sm:-right-2 ${isPending ? 'bg-yellow-500' : 'bg-blue-500'} text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full shadow-lg`}>
                                                                {isPending ? '⏳' : '✓'}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Pending unsettled indicator */}
                                                        {isPending && !displaySettled && (
                                                            <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-yellow-500 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full shadow-lg">
                                                                ⏳
                                                            </div>
                                                        )}
                                                        
                                                        {/* Header - compact on mobile */}
                                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 sm:mb-3 pt-1 sm:pt-2">
                                                            <div className="pl-5 sm:pl-6 mb-1 sm:mb-0">
                                                                <p className="text-[10px] sm:text-xs text-zinc-500 font-mono">
                                                                    {formatDate(sc.created_at)}
                                                                </p>
                                                                <p className="text-[10px] sm:text-xs text-zinc-600">
                                                                    by <span className="font-bold uppercase">{sc.created_by_username.toUpperCase()}</span>
                                                                </p>
                                                            </div>
                                                            <div className="flex items-center gap-1 sm:gap-2 pl-5 sm:pl-0">
                                                                {/* Settlement Checkbox - only for creator */}
                                                                {isCreator && (
                                                                    <button
                                                                        onClick={() => handleToggleSettlement(sc.id, originalSettled)}
                                                                        className={`flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs transition-all ${
                                                                            displaySettled 
                                                                                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                                                                                : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-blue-500/50'
                                                                        } ${isPending ? 'ring-1 ring-yellow-500' : ''}`}
                                                                        title={displaySettled ? 'Klik untuk membatalkan settlement' : 'Klik untuk menandai sudah settlement'}
                                                                        data-testid={`settle-btn-${sc.id}`}
                                                                    >
                                                                        {displaySettled ? '✓' : '☐'}
                                                                    </button>
                                                                )}
                                                                {sc.can_edit && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => handleOpenEditScoreCard(sc)}
                                                                        className="border-neon-green/30 text-neon-green hover:bg-neon-green/10 h-6 sm:h-8 px-1.5 sm:px-2 text-[10px] sm:text-xs"
                                                                        data-testid={`edit-scorecard-btn-${sc.id}`}
                                                                    >
                                                                        <Edit2 className="w-3 h-3" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Score Entries - compact on mobile */}
                                                        <div className="space-y-1 sm:space-y-2">
                                                            {sc.entries.map((entry, idx) => {
                                                                const isMyEntry = entry.user_id === user?.id;
                                                                const isFinanceSettled = sc.finance_settled?.includes(entry.user_id) || false;
                                                                const myFinanceStatus = isMyEntry ? getFinanceSettledStatus(sc.id, sc.finance_settled) : isFinanceSettled;
                                                                const hasMyFinancePending = isMyEntry && hasFinancePendingChange(sc.id);
                                                                
                                                                return (
                                                                    <div 
                                                                        key={idx} 
                                                                        className={`flex items-center justify-between py-1 px-1 rounded border-b border-zinc-800/50 last:border-0 w-full ${
                                                                            isFinanceSettled || myFinanceStatus ? 'bg-yellow-500/20' : ''
                                                                        } ${hasMyFinancePending ? 'ring-1 ring-orange-500' : ''}`}
                                                                    >
                                                                        <div className="flex items-center gap-1 min-w-0 flex-1">
                                                                            {/* Finance checkbox */}
                                                                            {user?.is_finance && isMyEntry && (
                                                                                <button
                                                                                    onClick={() => handleToggleFinanceSettled(sc.id, sc.finance_settled?.includes(user?.id))}
                                                                                    className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] flex-shrink-0 ${
                                                                                        myFinanceStatus
                                                                                            ? 'bg-yellow-500 border-yellow-500 text-black'
                                                                                            : 'border-zinc-600 text-zinc-500 hover:border-yellow-500'
                                                                                    }`}
                                                                                    data-testid={`finance-settle-btn-${sc.id}`}
                                                                                >
                                                                                    {myFinanceStatus ? '✓' : ''}
                                                                                </button>
                                                                            )}
                                                                            <span className={`text-xs font-bold uppercase truncate ${isMyEntry ? 'text-neon-green' : 'text-white'}`}>
                                                                                {entry.username}
                                                                            </span>
                                                                            {isFinanceSettled && (
                                                                                <span className="text-[10px] text-yellow-400">💰</span>
                                                                            )}
                                                                        </div>
                                                                        <span className={`text-xs font-mono font-bold flex-shrink-0 ml-1 ${
                                                                            entry.score > 0 ? 'text-neon-green' : 
                                                                            entry.score < 0 ? 'text-red-400' : 'text-zinc-400'
                                                                        }`}>
                                                                            {entry.score > 0 ? '+' : ''}{formatNumber(entry.score)}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {leaderboard.scorecards.length === 0 && (
                                    <div className="text-center py-8 text-zinc-500">
                                        <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                        <p>No score cards for {MONTH_NAMES[selectedMonth - 1]} {selectedYear}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Tournament Tab */}
                {activeTab === 'tournament' && (
                    <UserTournament 
                        token={token}
                        onError={setError}
                        onSuccess={setSuccess}
                    />
                )}

                {/* Transactions Tab */}
                {activeTab === 'transactions' && (
                    <div>
                        <div className="page-header flex items-start justify-between">
                            <div>
                                <h1 className="page-title">Transactions</h1>
                                <p className="page-subtitle">View transfers in your groups</p>
                            </div>
                            <Button
                                onClick={loadData}
                                variant="outline"
                                size="sm"
                                className="border-zinc-700 text-zinc-400 hover:text-white"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <span className="spinner" />
                            </div>
                        ) : transactions.length === 0 ? (
                            <div className="empty-state">
                                <History className="empty-state-icon mx-auto" />
                                <p>No transactions in your groups yet</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-lg border border-zinc-800">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Time</th>
                                            <th>From</th>
                                            <th>To</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactions.map((tx) => (
                                            <tr 
                                                key={tx.id} 
                                                data-testid={`transaction-row-${tx.id}`}
                                                className={tx.from_user_id === user?.id || tx.to_user_id === user?.id ? 'bg-neon-green/5' : ''}
                                            >
                                                <td>{formatDate(tx.created_at)}</td>
                                                <td>{formatTime(tx.created_at)}</td>
                                                <td className={`${tx.from_user_id === user?.id ? 'text-neon-green' : 'text-zinc-300'}`}>
                                                    {tx.from_username}
                                                    {tx.from_user_id === user?.id && ' (You)'}
                                                </td>
                                                <td className={`${tx.to_user_id === user?.id ? 'text-neon-green' : 'text-zinc-300'}`}>
                                                    {tx.to_username}
                                                    {tx.to_user_id === user?.id && ' (You)'}
                                                </td>
                                                <td>
                                                    <CurrencyDisplay 
                                                        amount={tx.amount} 
                                                        size="sm" 
                                                        className={tx.to_user_id === user?.id ? 'text-neon-green' : tx.from_user_id === user?.id ? 'text-red-400' : 'text-zinc-400'} 
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div>
                        <div className="page-header">
                            <h1 className="page-title flex items-center gap-2">
                                <Settings className="w-6 h-6 text-zinc-400" />
                                Pengaturan
                            </h1>
                            <p className="page-subtitle">Kelola akun dan integrasi</p>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            {/* Telegram Integration */}
                            <TelegramLink token={token} />

                            {/* Account Info */}
                            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                                <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                                    <Users className="w-5 h-5 text-zinc-400" />
                                    Info Akun
                                </h3>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-xs text-zinc-500">Username</p>
                                        <p className="text-white">{user?.username}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-zinc-500">Saldo</p>
                                        <CurrencyDisplay amount={balance} className="text-neon-green text-lg" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-zinc-500">Grup</p>
                                        <p className="text-white">{groups.length} grup</p>
                                    </div>
                                </div>
                            </div>

                            {/* Theme Settings */}
                            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                                <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                                    {isDark ? <Moon className="w-5 h-5 text-zinc-400" /> : <Sun className="w-5 h-5 text-yellow-400" />}
                                    Tampilan
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-zinc-300">Mode Gelap</span>
                                        <Button
                                            onClick={toggleTheme}
                                            variant="outline"
                                            size="sm"
                                            className="border-zinc-700"
                                        >
                                            {isDark ? 'On' : 'Off'}
                                        </Button>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-zinc-300">Fun Mode</span>
                                        <FunModeToggle />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-zinc-300">Virtual Numpad</span>
                                        <VirtualNumpadToggle enabled={useVirtualNumpad} onToggle={toggleVirtualNumpad} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            {/* Transfer Modal */}
            <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="font-headings">Transfer Balance</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleTransferSubmit} className="space-y-4">
                        <p className="text-sm text-zinc-400">
                            Your balance: <CurrencyDisplay amount={balance} size="sm" className="text-neon-green" />
                        </p>
                        
                        <div>
                            <Label htmlFor="recipient" className="form-label">Recipient</Label>
                            <Select value={selectedRecipient} onValueChange={setSelectedRecipient}>
                                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="select-recipient-trigger">
                                    <SelectValue placeholder="Select a user" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700">
                                    {getAllGroupMembers().map(member => (
                                        <SelectItem key={member.id} value={member.id} data-testid={`select-recipient-${member.id}`}>
                                            {member.username}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label htmlFor="amount" className="form-label">Amount</Label>
                            <div className="relative">
                                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                    <CurrencySymbol size="sm" className="text-zinc-500" />
                                </div>
                                <Input
                                    id="amount"
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={balance}
                                    value={transferAmount}
                                    onChange={(e) => setTransferAmount(e.target.value)}
                                    placeholder="0.00"
                                    className="bg-zinc-800 border-zinc-700 pl-8"
                                    data-testid="transfer-amount-input"
                                    required
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowTransfer(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={!selectedRecipient || !transferAmount}
                                className="flex-1 bg-neon-green hover:bg-neon-green/90 text-void-black"
                                data-testid="transfer-submit"
                            >
                                Continue
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* PIN Modal */}
            <Dialog open={showPinModal} onOpenChange={setShowPinModal}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="font-headings">Enter PIN</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6 py-4">
                        <div className="text-center">
                            <p className="text-sm text-zinc-400 mb-2">
                                Transferring <CurrencyDisplay amount={parseFloat(transferAmount) || 0} size="md" className="text-neon-green" />
                            </p>
                            <p className="text-sm text-zinc-500">
                                To: <span className="text-white">{getRecipientUsername(selectedRecipient)}</span>
                            </p>
                        </div>
                        
                        <div>
                            <p className="text-center text-sm text-zinc-400 mb-4">Enter your 4-digit PIN to confirm</p>
                            <PinInput 
                                length={4} 
                                onComplete={handlePinComplete}
                                disabled={transferLoading}
                            />
                        </div>

                        {transferLoading && (
                            <div className="flex items-center justify-center gap-2 text-zinc-400">
                                <span className="spinner" />
                                <span>Processing transfer...</span>
                            </div>
                        )}

                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowPinModal(false)}
                            className="w-full border-zinc-700"
                            disabled={transferLoading}
                        >
                            Cancel
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Create Score Card Modal */}
            <Dialog open={showCreateScoreCard} onOpenChange={setShowCreateScoreCard}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[85vh] overflow-y-auto p-4 sm:p-6">
                    <DialogHeader className="pb-2">
                        <DialogTitle className="font-headings text-base sm:text-lg">Buat Score Card</DialogTitle>
                        {/* Today's date display - Jakarta timezone */}
                        <p className="text-sm sm:text-base font-bold text-cyan-400" style={{textShadow: '0 0 8px #22d3ee'}}>
                            {formatJakartaDate()} (WIB)
                        </p>
                    </DialogHeader>
                    <div className="space-y-3">
                        {/* Virtual Numpad Toggle */}
                        <div className="flex items-center justify-between gap-2">
                            <p className="text-xs sm:text-sm text-zinc-400">
                                Total harus = 0
                            </p>
                            <VirtualNumpadToggle enabled={useVirtualNumpad} onToggle={toggleVirtualNumpad} />
                        </div>

                        {/* Date Selection */}
                        <div>
                            <Label className="form-label mb-2 block flex items-center gap-2 text-sm">
                                <Calendar className="w-4 h-4 text-neon-green" />
                                Tanggal Score Card
                            </Label>
                            <Input
                                type="date"
                                value={scoreCardDate}
                                onChange={(e) => setScoreCardDate(e.target.value)}
                                max={getJakartaDateString()}
                                className="bg-zinc-800 border-zinc-700"
                                style={{ fontSize: '16px' }}
                                data-testid="scorecard-date-input"
                            />
                            <p className="text-xs text-zinc-500 mt-1">
                                Zona waktu Jakarta (WIB). Maks: hari ini.
                            </p>
                        </div>

                        {/* User Selection */}
                        <div>
                            <Label className="form-label mb-2 block text-sm">Pilih User</Label>
                            <div className="space-y-1 max-h-[150px] overflow-y-auto border border-zinc-800 rounded-md p-2">
                                {getGroupMembers(scoreCardGroupId).map(member => (
                                    <label 
                                        key={member.id} 
                                        className="flex items-center gap-2 p-1.5 rounded-md hover:bg-zinc-800 cursor-pointer"
                                    >
                                        <Checkbox
                                            checked={selectedUsers.some(u => u.id === member.id)}
                                            onCheckedChange={() => handleUserSelect(member.id, member.username)}
                                            data-testid={`select-user-checkbox-${member.id}`}
                                        />
                                        <span className="text-sm text-zinc-300">{member.username}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Score Inputs */}
                        {selectedUsers.length > 0 && (
                            <div>
                                <Label className="form-label mb-2 block text-sm">Skor (maks 1 desimal)</Label>
                                <div className="space-y-2">
                                    {selectedUsers.map(u => (
                                        <ScoreInputWithNumpad
                                            key={u.id}
                                            userId={u.id}
                                            username={u.username}
                                            value={scoreInputs[u.id] || ''}
                                            onChange={handleScoreChange}
                                            useVirtualNumpad={useVirtualNumpad}
                                            onToggleMinus={toggleScoreMinus}
                                        />
                                    ))}
                                </div>

                                {/* Total Score Display - Compact */}
                                <div className={`mt-3 p-2 rounded-md border ${isScoreTotalZero(scoreInputs) ? 'bg-neon-green/10 border-neon-green/30' : 'bg-red-500/10 border-red-500/30'}`}>
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-zinc-400">Total:</span>
                                        <span className={`text-base font-mono font-bold ${isScoreTotalZero(scoreInputs) ? 'text-neon-green' : 'text-red-400'}`}>
                                            {roundTo1Decimal(calculateTotal(scoreInputs))}
                                        </span>
                                    </div>
                                    {!isScoreTotalZero(scoreInputs) && (
                                        <p className="text-xs text-red-400 mt-0.5 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" />
                                            Harus = 0
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Affects Balance Toggle */}
                        <div className="p-3 rounded-md border border-zinc-700 bg-zinc-800/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Wallet className="w-4 h-4 text-yellow-500" />
                                    <div>
                                        <span className="text-sm text-white">Aktifkan ke Saldo</span>
                                        <p className="text-xs text-zinc-500">Skor akan mengubah saldo pemain</p>
                                    </div>
                                </div>
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => setAffectsBalance(!affectsBalance)}
                                    className={`${affectsBalance 
                                        ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' 
                                        : 'bg-zinc-800 text-zinc-400 border-zinc-700'} border`}
                                >
                                    {affectsBalance ? 'ON' : 'OFF'}
                                </Button>
                            </div>
                            {groupSettings?.tk_enabled && affectsBalance && (
                                <p className="text-xs text-yellow-500 mt-2 flex items-center gap-1">
                                    <PiggyBank className="w-3 h-3" />
                                    TK aktif - pemenang tertinggi akan dipotong otomatis
                                </p>
                            )}
                        </div>

                        <div className="flex gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setShowCreateScoreCard(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Batal
                            </Button>
                            <Button
                                onClick={handleCreateScoreCard}
                                size="sm"
                                disabled={selectedUsers.length === 0 || createScoreCardLoading || !isScoreTotalZero(scoreInputs)}
                                className="flex-1 bg-neon-green hover:bg-neon-green/90 text-void-black disabled:opacity-50"
                                data-testid="create-scorecard-submit"
                            >
                                {createScoreCardLoading ? (
                                    <span className="flex items-center gap-2">
                                        <span className="spinner w-4 h-4" />
                                        ...
                                    </span>
                                ) : (
                                    'Simpan'
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Score Card Modal */}
            <Dialog open={showEditScoreCard} onOpenChange={setShowEditScoreCard}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-headings">Edit Score Card</DialogTitle>
                    </DialogHeader>
                    {editingScoreCard && (
                        <div className="space-y-4">
                            {/* Virtual Numpad Toggle */}
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-zinc-400">
                                    Edit skor. Total harus = 0.
                                </p>
                                <VirtualNumpadToggle enabled={useVirtualNumpad} onToggle={toggleVirtualNumpad} />
                            </div>

                            {/* Score Inputs */}
                            <div>
                                <Label className="form-label mb-3 block">Skor</Label>
                                <div className="space-y-3">
                                    {editingScoreCard.entries.map(entry => (
                                        <ScoreInputWithNumpad
                                            key={entry.user_id}
                                            userId={entry.user_id}
                                            username={entry.username}
                                            value={editScoreInputs[entry.user_id] || ''}
                                            onChange={handleEditScoreChange}
                                            useVirtualNumpad={useVirtualNumpad}
                                            onToggleMinus={toggleEditScoreMinus}
                                        />
                                    ))}
                                </div>

                                {/* Total Score Display */}
                                <div className={`mt-4 p-3 rounded-md border ${isScoreTotalZero(editScoreInputs) ? 'bg-neon-green/10 border-neon-green/30' : 'bg-red-500/10 border-red-500/30'}`}>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-zinc-400">Total Skor:</span>
                                        <span className={`text-lg font-mono font-bold ${isScoreTotalZero(editScoreInputs) ? 'text-neon-green' : 'text-red-400'}`}>
                                            {roundTo1Decimal(calculateTotal(editScoreInputs))}
                                        </span>
                                    </div>
                                    {!isScoreTotalZero(editScoreInputs) && (
                                        <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" />
                                            Total harus sama dengan 0
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-2 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setShowEditScoreCard(false);
                                        setEditingScoreCard(null);
                                        setEditScoreInputs({});
                                    }}
                                    className="flex-1 border-zinc-700"
                                >
                                    Batal
                                </Button>
                                <Button
                                    onClick={handleUpdateScoreCard}
                                    disabled={editScoreCardLoading || !isScoreTotalZero(editScoreInputs)}
                                    className="flex-1 bg-neon-green hover:bg-neon-green/90 text-void-black disabled:opacity-50"
                                    data-testid="edit-scorecard-submit"
                                >
                                    {editScoreCardLoading ? (
                                        <span className="flex items-center gap-2">
                                            <span className="spinner w-4 h-4" />
                                            Menyimpan...
                                        </span>
                                    ) : (
                                        'Simpan Perubahan'
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Settlement Modal (Operator Only) */}
            <Dialog open={showSettleModal} onOpenChange={setShowSettleModal}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-headings flex items-center gap-2">
                            <ArrowLeftRight className="w-5 h-5 text-blue-400" />
                            Settlement - {selectedGroup?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {(() => {
                            // Filter only non-zero balance entries
                            const nonZeroEntries = leaderboard?.entries?.filter(e => e.balance !== 0) || [];
                            const settlementSim = calculateSettlementSimulation(nonZeroEntries);
                            
                            return (
                                <>
                                    {/* Settlement Simulation Preview */}
                                    {settlementSim.length > 0 && (
                                        <div className="p-3 bg-zinc-800/30 rounded border border-zinc-700">
                                            <h4 className="text-xs font-semibold text-zinc-400 mb-2 flex items-center gap-2">
                                                <ArrowLeftRight className="w-3 h-3" />
                                                Simulasi ({settlementSim.length} transfer)
                                            </h4>
                                            <div className="space-y-1">
                                                {settlementSim.map((transfer, idx) => (
                                                    <div key={idx} className="flex items-center gap-1 text-xs">
                                                        <span className="text-red-400 uppercase font-medium">{transfer.from}</span>
                                                        <span className="text-zinc-500">→</span>
                                                        <span className="text-yellow-500 font-mono font-bold">{formatNumber(transfer.amount)}</span>
                                                        <span className="text-zinc-500">→</span>
                                                        <span className="text-emerald-400 uppercase font-medium">{transfer.to}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <p className="text-sm text-zinc-400">
                                        Catat pembayaran antar pemain. Total harus = 0.
                                    </p>
                                    
                                    {/* Settlement Entries - Only non-zero balance */}
                                    <div className="space-y-2">
                                        {nonZeroEntries.length === 0 ? (
                                            <p className="text-zinc-500 text-sm text-center py-4">Semua saldo sudah 0, tidak perlu settlement.</p>
                                        ) : (
                                            nonZeroEntries
                                                .sort((a, b) => b.balance - a.balance)
                                                .map(entry => (
                                                    <div key={entry.user_id} className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded">
                                                        <span className="w-24 text-sm text-white uppercase truncate">{entry.username}</span>
                                                        <span className={`w-16 text-xs font-mono ${
                                                            (entry.balance || 0) >= 0 ? 'text-neon-green' : 'text-red-400'
                                                        }`}>
                                                            ({formatNumber(entry.balance || 0)})
                                                        </span>
                                                        <Input
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={settleEntries[entry.user_id] || ''}
                                                            onChange={(e) => setSettleEntries(prev => ({
                                                                ...prev,
                                                                [entry.user_id]: e.target.value.replace(',', '.')
                                                            }))}
                                                            placeholder="0"
                                                            className="flex-1 bg-zinc-800 border-zinc-700 text-[16px]"
                                                        />
                                                    </div>
                                                ))
                                        )}
                                    </div>
                                </>
                            );
                        })()}

                        {/* Total Display */}
                        <div className={`p-3 rounded-md border ${
                            Math.abs(Object.values(settleEntries).reduce((sum, v) => sum + (parseFloat(v) || 0), 0)) < 0.01
                                ? 'bg-neon-green/10 border-neon-green/30'
                                : 'bg-red-500/10 border-red-500/30'
                        }`}>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-zinc-400">Total:</span>
                                <span className={`text-lg font-mono font-bold ${
                                    Math.abs(Object.values(settleEntries).reduce((sum, v) => sum + (parseFloat(v) || 0), 0)) < 0.01
                                        ? 'text-neon-green'
                                        : 'text-red-400'
                                }`}>
                                    {roundTo1Decimal(Object.values(settleEntries).reduce((sum, v) => sum + (parseFloat(v) || 0), 0))}
                                </span>
                            </div>
                        </div>

                        {/* Note */}
                        <div>
                            <Label className="text-xs text-zinc-500 mb-1 block">Catatan (opsional)</Label>
                            <Input
                                value={settleNote}
                                onChange={(e) => setSettleNote(e.target.value)}
                                placeholder="Contoh: Pembayaran via transfer"
                                className="bg-zinc-800 border-zinc-700 text-[16px]"
                            />
                        </div>

                        <div className="flex gap-2 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowSettleModal(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Batal
                            </Button>
                            <Button
                                onClick={handleCreateSettlement}
                                disabled={settleLoading}
                                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                            >
                                {settleLoading ? 'Menyimpan...' : 'Simpan Settlement'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Bulk Balance Modal (Operator Only) */}
            <Dialog open={showBulkBalanceModal} onOpenChange={setShowBulkBalanceModal}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-headings flex items-center gap-2">
                            <Wallet className="w-5 h-5 text-yellow-500" />
                            Set Saldo - {selectedGroup?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-zinc-400">
                            Pilih pemain dan atur saldo awal atau tambah saldo.
                        </p>
                        
                        {/* Operation Type */}
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                onClick={() => setBulkBalanceOperation('set')}
                                className={`flex-1 ${bulkBalanceOperation === 'set'
                                    ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                                    : 'bg-zinc-800 text-zinc-400 border-zinc-700'} border`}
                            >
                                Set Saldo
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => setBulkBalanceOperation('add')}
                                className={`flex-1 ${bulkBalanceOperation === 'add'
                                    ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                                    : 'bg-zinc-800 text-zinc-400 border-zinc-700'} border`}
                            >
                                Tambah Saldo
                            </Button>
                        </div>

                        {/* Player Selection */}
                        <div>
                            <Label className="text-xs text-zinc-500 mb-2 block">
                                Pilih Pemain ({bulkBalanceUsers.length} dipilih)
                            </Label>
                            <div className="grid grid-cols-2 gap-2">
                                {leaderboard?.entries?.map(entry => (
                                    <button
                                        key={entry.user_id}
                                        onClick={() => toggleBulkBalanceUser(entry.user_id)}
                                        className={`p-2 rounded border text-left text-sm transition-all ${
                                            bulkBalanceUsers.includes(entry.user_id)
                                                ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                                                : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            {bulkBalanceUsers.includes(entry.user_id) && <CheckCircle className="w-3 h-3" />}
                                            <span className="uppercase">{entry.username}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setBulkBalanceUsers(leaderboard?.entries?.map(e => e.user_id) || [])}
                                className="mt-2 text-xs text-zinc-500"
                            >
                                Pilih Semua
                            </Button>
                        </div>

                        {/* Amount Input */}
                        <div>
                            <Label className="text-xs text-zinc-500 mb-1 block">
                                {bulkBalanceOperation === 'set' ? 'Saldo Awal' : 'Tambah Saldo'}
                            </Label>
                            <Input
                                type="number"
                                step="0.1"
                                value={bulkBalanceAmount}
                                onChange={(e) => setBulkBalanceAmount(e.target.value)}
                                placeholder="0"
                                className="bg-zinc-800 border-zinc-700 text-[16px]"
                            />
                            <p className="text-xs text-zinc-500 mt-1">Boleh negatif, 0, atau positif</p>
                        </div>

                        <div className="flex gap-2 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowBulkBalanceModal(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Batal
                            </Button>
                            <Button
                                onClick={handleBulkBalance}
                                disabled={settleLoading || bulkBalanceUsers.length === 0}
                                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                            >
                                {settleLoading ? 'Menyimpan...' : 'Update Saldo'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Balance Overview Modal with Settlement Simulation */}
            <Dialog open={showBalanceModal} onOpenChange={setShowBalanceModal}>
                <DialogContent className="bg-void-black border border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <PiggyBank className="w-5 h-5 text-emerald-400" />
                            Balance Overview - {selectedGroup?.name}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        {/* Non-zero balance members */}
                        {(() => {
                            const nonZeroEntries = leaderboard?.entries?.filter(e => e.balance !== 0) || [];
                            const settlementSim = calculateSettlementSimulation(nonZeroEntries);
                            
                            return (
                                <>
                                    {/* Balance List */}
                                    <div>
                                        <h4 className="text-sm font-semibold text-zinc-400 mb-2">
                                            Saldo Aktif ({nonZeroEntries.length} member)
                                        </h4>
                                        {nonZeroEntries.length === 0 ? (
                                            <p className="text-zinc-500 text-sm">Semua saldo sudah 0</p>
                                        ) : (
                                            <div className="space-y-1">
                                                {nonZeroEntries
                                                    .sort((a, b) => b.balance - a.balance)
                                                    .map((entry, idx) => (
                                                        <div 
                                                            key={entry.user_id} 
                                                            className={`flex justify-between items-center p-2 rounded ${
                                                                entry.balance > 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'
                                                            }`}
                                                        >
                                                            <span className="font-medium text-white uppercase">
                                                                {entry.username}
                                                            </span>
                                                            <span className={`font-mono font-bold ${
                                                                entry.balance > 0 ? 'text-emerald-400' : 'text-red-400'
                                                            }`}>
                                                                {entry.balance > 0 ? '+' : ''}{formatNumber(entry.balance)}
                                                            </span>
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Settlement Simulation */}
                                    {settlementSim.length > 0 && (
                                        <div className="border-t border-zinc-700 pt-4">
                                            <h4 className="text-sm font-semibold text-zinc-400 mb-2 flex items-center gap-2">
                                                <ArrowLeftRight className="w-4 h-4" />
                                                Simulasi Settlement ({settlementSim.length} transfer)
                                            </h4>
                                            <p className="text-xs text-zinc-500 mb-3">
                                                Jalur terpendek: yang kalah dipasangkan dengan pemenang terdekat untuk settlement lebih cepat.
                                            </p>
                                            <div className="space-y-2">
                                                {settlementSim.map((transfer, idx) => (
                                                    <div 
                                                        key={idx}
                                                        className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded border border-zinc-700"
                                                    >
                                                        <span className="font-medium text-red-400 uppercase min-w-[80px]">
                                                            {transfer.from}
                                                        </span>
                                                        <div className="flex-1 flex items-center justify-center">
                                                            <div className="flex items-center gap-1 text-yellow-500">
                                                                <span className="text-xs">───</span>
                                                                <span className="font-mono font-bold text-sm">
                                                                    {formatNumber(transfer.amount)}
                                                                </span>
                                                                <span className="text-xs">→</span>
                                                            </div>
                                                        </div>
                                                        <span className="font-medium text-emerald-400 uppercase min-w-[80px] text-right">
                                                            {transfer.to}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Summary */}
                                            <div className="mt-3 p-2 bg-zinc-800/30 rounded text-xs text-zinc-400">
                                                <p className="font-semibold mb-1">Ringkasan:</p>
                                                {(() => {
                                                    // Group by sender
                                                    const senderCounts = {};
                                                    settlementSim.forEach(t => {
                                                        if (!senderCounts[t.from]) senderCounts[t.from] = 0;
                                                        senderCounts[t.from]++;
                                                    });
                                                    return Object.entries(senderCounts).map(([name, count]) => (
                                                        <p key={name}>• {name.toUpperCase()}: {count}x transfer</p>
                                                    ));
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </>
                            );
                        })()}

                        <div className="flex gap-2 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowBalanceModal(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Tutup
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UserDashboard;
